<template>
  <div class="departments-layout">
    <!-- Sidebar -->
    <DemoSidebar />

    <!-- Main Content -->
    <div class="main-content">
      <!-- Top Header -->
      <DemoTopHeader />

      <!-- Page Content -->
      <main class="page-content">
        <!-- Add Button -->
        <div class="add-button-container">
          <button @click="openAddModal" class="add-btn">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
            </svg>
            Add Department
          </button>
        </div>

        <!-- Stats Overview -->
        <div class="stats-overview">
          <div class="stat-card">
            <div class="stat-icon">🏢</div>
            <div class="stat-content">
              <div class="stat-number">{{ departments.length }}</div>
              <div class="stat-label">Total Departments</div>
            </div>
          </div>
        </div>

        <!-- Departments Table -->
        <div class="departments-table-container">
          <div class="table-card">
            <div class="table-header">
              <h3>Departments</h3>
            </div>

            <div class="table-content">
              <table class="departments-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Department Name</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="department in departments" :key="department.id">
                    <td class="id-cell">{{ department.id }}</td>
                    <td class="name-cell">{{ department.name }}</td>
                    <td class="actions-cell">
                      <div class="actions-group">
                        <button @click="editDepartment(department)" class="action-btn edit-btn" title="Edit Department">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
                          </svg>
                        </button>
                        <button @click="deleteDepartment(department)" class="action-btn delete-btn" title="Delete Department">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <!-- Loading State -->
        <div v-if="loading" class="loading-state">
          <div class="loading-spinner"></div>
          <p>Loading departments...</p>
        </div>

        <!-- Empty State -->
        <div v-if="!loading && departments.length === 0" class="empty-state">
          <div class="empty-icon">🏢</div>
          <h3>No departments found</h3>
          <p>Create your first department to get started.</p>
          <button @click="openAddModal" class="empty-action-btn">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
            </svg>
            Add Department
          </button>
        </div>

        <!-- Add/Edit Department Modal -->
        <DepartmentModal
          :visible="showModal"
          :department="selectedDepartment"
          :is-edit="isEdit"
          @close="closeModal"
          @save="handleSaveDepartment"
        />

        <!-- Delete Confirmation Modal -->
        <DeleteDepartmentModal
          :visible="showDeleteModal"
          :department="selectedDepartment"
          @close="showDeleteModal = false"
          @confirm="handleDeleteDepartment"
        />

        <!-- Notification Toast -->
        <div v-if="notification" class="notification-toast" :class="notification.type">
          <div class="notification-icon">
            <svg v-if="notification.type === 'success'" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
            </svg>
            <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M11 15h2v2h-2zm0-8h2v6h-2zm.99-5C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8z"/>
            </svg>
          </div>
          <div class="notification-content">
            <div class="notification-title">{{ notification.title }}</div>
            <div class="notification-message">{{ notification.message }}</div>
          </div>
          <button @click="dismissNotification" class="notification-close">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
            </svg>
          </button>
        </div>
      </main>
    </div>
  </div>
</template>

<script>
import DemoSidebar from '../components/DemoSidebar.vue'
import DemoTopHeader from '../components/DemoTopHeader.vue'
import DepartmentModal from '../components/DepartmentModal.vue'
import DeleteDepartmentModal from '../components/DeleteDepartmentModal.vue'

export default {
  name: 'DemoDepartmentsView',
  components: {
    DemoSidebar,
    DemoTopHeader,
    DepartmentModal,
    DeleteDepartmentModal
  },
  data () {
    return {
      departments: [
        { id: 1, name: 'Sales' },
        { id: 2, name: 'Customer Support' },
        { id: 3, name: 'Technical Support' },
        { id: 4, name: 'Marketing' },
        { id: 5, name: 'Product Development' },
        { id: 6, name: 'Human Resources' }
      ],
      loading: false,
      showModal: false,
      showDeleteModal: false,
      selectedDepartment: null,
      isEdit: false,
      notification: null
    }
  },
  async mounted () {
    await this.loadDepartments()
  },
  methods: {
    async loadDepartments () {
      // Simulate loading with dummy data
      this.loading = true

      // Simulate network delay
      await new Promise(resolve => setTimeout(resolve, 500))

      // Dummy data is already set in data()
      this.loading = false
    },

    openAddModal () {
      this.selectedDepartment = null
      this.isEdit = false
      this.showModal = true
    },

    editDepartment (department) {
      this.selectedDepartment = { ...department }
      this.isEdit = true
      this.showModal = true
    },

    closeModal () {
      this.showModal = false
      this.selectedDepartment = null
      this.isEdit = false
    },

    deleteDepartment (department) {
      this.selectedDepartment = { ...department }
      this.showDeleteModal = true
    },

    async handleDeleteDepartment (departmentId) {
      try {
        // Simulate API call with delay
        await new Promise(resolve => setTimeout(resolve, 300))

        // Remove department from dummy data
        this.departments = this.departments.filter(d => d.id !== departmentId)

        this.showDeleteModal = false
        this.showNotification({
          type: 'success',
          title: 'Department Deleted',
          message: 'Department deleted successfully'
        })
      } catch (error) {
        console.error('Error deleting department:', error)
        this.showNotification({
          type: 'error',
          title: 'Delete Failed',
          message: error.message
        })
      }
    },

    async handleSaveDepartment (data) {
      try {
        // Simulate API call with delay
        await new Promise(resolve => setTimeout(resolve, 300))

        if (this.isEdit) {
          // Update existing department
          const index = this.departments.findIndex(d => d.id === data.id)
          if (index !== -1) {
            this.departments[index] = { ...data }
          }
        } else {
          // Add new department with auto-generated ID
          const newId = Math.max(...this.departments.map(d => d.id), 0) + 1
          this.departments.push({
            id: newId,
            name: data.name
          })
        }

        this.closeModal()
        this.showNotification({
          type: 'success',
          title: `Department ${this.isEdit ? 'Updated' : 'Added'}`,
          message: `Department ${this.isEdit ? 'updated' : 'added'} successfully`
        })
      } catch (error) {
        console.error('Error saving department:', error)
        this.showNotification({
          type: 'error',
          title: 'Save Failed',
          message: error.message
        })
      }
    },

    showNotification (notification) {
      this.notification = notification
      // Auto-dismiss after 5 seconds
      setTimeout(() => {
        this.dismissNotification()
      }, 5000)
    },

    dismissNotification () {
      this.notification = null
    }
  }
}
</script>

<style scoped>
.departments-layout {
  display: flex;
  min-height: 100vh;
  background: var(--bg-secondary);
  transition: background-color 0.3s ease;
}

.main-content {
  flex: 1;
  margin-left: 280px;
  display: flex;
  flex-direction: column;
}

.page-content {
  flex: 1;
  padding: 32px;
}

.add-button-container {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 24px;
}

.add-btn, .empty-action-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #3b82f6;
  color: white;
  border: none;
  padding: 12px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.add-btn:hover, .empty-action-btn:hover {
  background: #2563eb;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
}

.stats-overview {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 20px;
  margin-bottom: 32px;
}

.stat-card {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 24px;
  background: var(--bg-primary);
  border-radius: 12px;
  border: 1px solid var(--border-primary);
  transition: background-color 0.3s ease, border-color 0.3s ease;
}

.stat-icon {
  font-size: 32px;
  opacity: 0.8;
}

.stat-number {
  font-size: 24px;
  font-weight: 700;
  color: var(--text-primary);
  line-height: 1;
  transition: color 0.3s ease;
}

.stat-label {
  font-size: 14px;
  color: var(--text-secondary);
  margin-top: 4px;
  transition: color 0.3s ease;
}

.departments-table-container {
  background: var(--bg-primary);
  border-radius: 16px;
  border: 1px solid var(--border-primary);
  overflow: hidden;
  transition: background-color 0.3s ease, border-color 0.3s ease;
}

.table-card {
  width: 100%;
}

.table-header {
  padding: 24px 24px 16px;
  border-bottom: 1px solid var(--border-primary);
  transition: border-color 0.3s ease;
}

.table-header h3 {
  font-size: 20px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
  transition: color 0.3s ease;
}

.table-content {
  overflow-x: auto;
}

.departments-table {
  width: 100%;
  border-collapse: collapse;
}

.departments-table th {
  text-align: left;
  padding: 16px 24px;
  font-size: 14px;
  font-weight: 600;
  color: var(--text-secondary);
  background: var(--bg-tertiary);
  border-bottom: 1px solid var(--border-primary);
  transition: color 0.3s ease, background-color 0.3s ease, border-color 0.3s ease;
}

.departments-table td {
  padding: 16px 24px;
  border-bottom: 1px solid var(--border-secondary);
  transition: border-color 0.3s ease;
}

.departments-table tbody tr:hover {
  background: var(--bg-tertiary);
  transition: background-color 0.2s ease;
}

.id-cell {
  font-size: 14px;
  font-weight: 500;
  color: var(--text-secondary);
  font-family: monospace;
  transition: color 0.3s ease;
}

.name-cell {
  font-size: 16px;
  font-weight: 500;
  color: var(--text-primary);
  transition: color 0.3s ease;
}

.actions-cell {
  width: 120px;
}

.actions-group {
  display: flex;
  gap: 8px;
}

.action-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  background: var(--bg-tertiary);
  color: var(--text-secondary);
}

.action-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.edit-btn:hover {
  background: rgba(139, 92, 246, 0.1);
  color: #8b5cf6;
}

.delete-btn {
  background: rgba(239, 68, 68, 0.1);
  color: #ef4444;
}

.delete-btn:hover {
  background: #ef4444;
  color: white;
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(239, 68, 68, 0.3);
}

.loading-state, .empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 64px;
  color: var(--text-secondary);
  text-align: center;
  transition: color 0.3s ease;
}

.loading-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid var(--border-primary);
  border-top: 3px solid #3b82f6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  transition: border-color 0.3s ease;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.empty-icon {
  font-size: 48px;
  opacity: 0.5;
}

.empty-state h3 {
  font-size: 20px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
  transition: color 0.3s ease;
}

.empty-state p {
  font-size: 16px;
  color: var(--text-secondary);
  margin: 0;
  transition: color 0.3s ease;
}

/* Notification Toast */
.notification-toast {
  position: fixed;
  top: 20px;
  right: 20px;
  min-width: 320px;
  max-width: 400px;
  background: var(--bg-primary);
  border: 1px solid var(--border-primary);
  border-radius: 12px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  padding: 16px;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  z-index: 10000;
  animation: slideInRight 0.3s ease-out;
  transition: background-color 0.3s ease, border-color 0.3s ease;
}

.notification-toast.success {
  border-left: 4px solid #10b981;
}

.notification-toast.error {
  border-left: 4px solid #ef4444;
}

.notification-icon {
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 2px;
}

.notification-toast.success .notification-icon {
  background: rgba(16, 185, 129, 0.1);
  color: #10b981;
}

.notification-toast.error .notification-icon {
  background: rgba(239, 68, 68, 0.1);
  color: #ef4444;
}

.notification-content {
  flex: 1;
  min-width: 0;
}

.notification-title {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 4px;
  transition: color 0.3s ease;
}

.notification-message {
  font-size: 13px;
  color: var(--text-secondary);
  line-height: 1.4;
  transition: color 0.3s ease;
}

.notification-close {
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  border: none;
  background: none;
  color: var(--text-tertiary);
  cursor: pointer;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  transition: all 0.2s ease;
}

.notification-close:hover {
  background: var(--bg-secondary);
  color: var(--text-secondary);
}

@keyframes slideInRight {
  from {
    transform: translateX(400px);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

@media (max-width: 768px) {
  .main-content {
    margin-left: 0;
    padding-top: 70px;
  }

  .page-content {
    padding: 16px;
  }

  .page-header {
    flex-direction: column;
    gap: 20px;
    align-items: stretch;
    padding: 20px 16px;
  }

  .stats-overview {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  .stat-card {
    padding: 16px;
    gap: 12px;
  }

  .stat-icon {
    font-size: 24px;
  }

  .stat-number {
    font-size: 20px;
  }

  .table-header {
    padding: 16px;
  }

  .departments-table th,
  .departments-table td {
    padding: 12px 16px;
  }

  .notification-toast {
    top: 10px;
    right: 10px;
    left: 10px;
    min-width: auto;
    max-width: none;
  }
}
</style>
